import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/api/client';
import {
  assessmentService,
  assessmentScaleService,
  learningAreaService,
  learningOutcomeService,
  learningPeriodService
} from '../../services/api/assessments';
import { 
  Assessment, 
  AssessmentCreate, 
  AssessmentUpdate, 
  AssessmentFilters,
  AssessmentScale,
  AssessmentScaleCreate,
  AssessmentScaleUpdate,
  AssessmentSummary,
  LearningArea,
  LearningAreaCreate,
  LearningAreaUpdate,
  LearningOutcome,
  LearningOutcomeCreate,
  LearningOutcomeUpdate,
  LearningPeriod,
  LearningPeriodCreate,
  LearningPeriodUpdate,
  PaginatedResponse 
} from '../../types/api';

// Assessment hooks
export const useAssessments = (filters?: AssessmentFilters) => {
  return useQuery<PaginatedResponse<Assessment>>({
    queryKey: ['assessments', filters],
    queryFn: () => assessmentService.getAssessments(filters),
    placeholderData: (previousData) => previousData,
  });
};

export const useAssessment = (id: string, enabled = true) => {
  return useQuery<Assessment>({
    queryKey: ['assessment', id],
    queryFn: () => assessmentService.getAssessment(id),
    enabled: enabled && !!id,
  });
};

export const useCreateAssessment = () => {
  const queryClient = useQueryClient();
  
  return useMutation<Assessment, Error, AssessmentCreate>({
    mutationFn: assessmentService.createAssessment,
    onSuccess: (newAssessment) => {
      // Invalidate and refetch assessments
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
      // Update the cache with the new assessment
      queryClient.setQueryData(['assessment', newAssessment.id], newAssessment);
      // Invalidate child's assessment summary
      queryClient.invalidateQueries({ queryKey: ['assessment-summary', newAssessment.child_id] });
    },
  });
};

export const useUpdateAssessment = () => {
  const queryClient = useQueryClient();
  
  return useMutation<Assessment, Error, { id: string; data: AssessmentUpdate }>({
    mutationFn: ({ id, data }) => assessmentService.updateAssessment(id, data),
    onSuccess: (updatedAssessment) => {
      // Update the cache
      queryClient.setQueryData(['assessment', updatedAssessment.id], updatedAssessment);
      // Invalidate assessments list
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
      // Invalidate child's assessment summary
      queryClient.invalidateQueries({ queryKey: ['assessment-summary', updatedAssessment.child_id] });
    },
  });
};

export const useDeleteAssessment = () => {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, string>({
    mutationFn: assessmentService.deleteAssessment,
    onSuccess: (_, assessmentId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: ['assessment', assessmentId] });
      // Invalidate assessments list
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
      // Invalidate assessment summaries
      queryClient.invalidateQueries({ queryKey: ['assessment-summary'] });
    },
  });
};

export const usePublishAssessment = () => {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, string>({
    mutationFn: assessmentService.publishAssessment,
    onSuccess: (_, assessmentId) => {
      // Invalidate specific assessment
      queryClient.invalidateQueries({ queryKey: ['assessment', assessmentId] });
      // Invalidate assessments list
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
      // Invalidate assessment summaries
      queryClient.invalidateQueries({ queryKey: ['assessment-summary'] });
    },
  });
};

export const useUnpublishAssessment = () => {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, string>({
    mutationFn: assessmentService.unpublishAssessment,
    onSuccess: (_, assessmentId) => {
      // Invalidate specific assessment
      queryClient.invalidateQueries({ queryKey: ['assessment', assessmentId] });
      // Invalidate assessments list
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
      // Invalidate assessment summaries
      queryClient.invalidateQueries({ queryKey: ['assessment-summary'] });
    },
  });
};

export const useAssessmentSummary = (childId?: string, enabled = true) => {
  return useQuery<AssessmentSummary>({
    queryKey: ['assessment-summary', childId],
    queryFn: () => assessmentService.getAssessmentSummary(childId || ''),
    enabled: enabled && !!childId,
  });
};

// Overall assessment summary (for dashboard)
export const useOverallAssessmentSummary = () => {
  return useQuery<any>({
    queryKey: ['overall-assessment-summary'],
    queryFn: async () => {
      const response = await apiClient.get('/assessments/summary/overall');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Assessment Scale hooks
export const useAssessmentScales = () => {
  return useQuery<AssessmentScale[]>({
    queryKey: ['assessment-scales'],
    queryFn: assessmentScaleService.getAssessmentScales,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useAssessmentScale = (id: string, enabled = true) => {
  return useQuery<AssessmentScale>({
    queryKey: ['assessment-scale', id],
    queryFn: () => assessmentScaleService.getAssessmentScale(id),
    enabled: enabled && !!id,
  });
};

export const useCreateAssessmentScale = () => {
  const queryClient = useQueryClient();
  
  return useMutation<AssessmentScale, Error, AssessmentScaleCreate>({
    mutationFn: assessmentScaleService.createAssessmentScale,
    onSuccess: (newScale) => {
      // Invalidate and refetch scales
      queryClient.invalidateQueries({ queryKey: ['assessment-scales'] });
      // Update the cache with the new scale
      queryClient.setQueryData(['assessment-scale', newScale.id], newScale);
    },
  });
};

export const useUpdateAssessmentScale = () => {
  const queryClient = useQueryClient();
  
  return useMutation<AssessmentScale, Error, { id: string; data: AssessmentScaleUpdate }>({
    mutationFn: ({ id, data }) => assessmentScaleService.updateAssessmentScale(id, data),
    onSuccess: (updatedScale) => {
      // Update the cache
      queryClient.setQueryData(['assessment-scale', updatedScale.id], updatedScale);
      // Invalidate scales list
      queryClient.invalidateQueries({ queryKey: ['assessment-scales'] });
    },
  });
};

export const useDeleteAssessmentScale = () => {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, string>({
    mutationFn: assessmentScaleService.deleteAssessmentScale,
    onSuccess: (_, scaleId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: ['assessment-scale', scaleId] });
      // Invalidate scales list
      queryClient.invalidateQueries({ queryKey: ['assessment-scales'] });
    },
  });
};

// Learning Area hooks
export const useLearningAreas = () => {
  return useQuery<LearningArea[]>({
    queryKey: ['learning-areas'],
    queryFn: learningAreaService.getLearningAreas,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useLearningArea = (id: string, enabled = true) => {
  return useQuery<LearningArea>({
    queryKey: ['learning-area', id],
    queryFn: () => learningAreaService.getLearningArea(id),
    enabled: enabled && !!id,
  });
};

export const useCreateLearningArea = () => {
  const queryClient = useQueryClient();
  
  return useMutation<LearningArea, Error, LearningAreaCreate>({
    mutationFn: learningAreaService.createLearningArea,
    onSuccess: (newArea) => {
      // Invalidate and refetch areas
      queryClient.invalidateQueries({ queryKey: ['learning-areas'] });
      // Update the cache with the new area
      queryClient.setQueryData(['learning-area', newArea.id], newArea);
    },
  });
};

export const useUpdateLearningArea = () => {
  const queryClient = useQueryClient();
  
  return useMutation<LearningArea, Error, { id: string; data: LearningAreaUpdate }>({
    mutationFn: ({ id, data }) => learningAreaService.updateLearningArea(id, data),
    onSuccess: (updatedArea) => {
      // Update the cache
      queryClient.setQueryData(['learning-area', updatedArea.id], updatedArea);
      // Invalidate areas list
      queryClient.invalidateQueries({ queryKey: ['learning-areas'] });
    },
  });
};

export const useDeleteLearningArea = () => {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, string>({
    mutationFn: learningAreaService.deleteLearningArea,
    onSuccess: (_, areaId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: ['learning-area', areaId] });
      // Invalidate areas list
      queryClient.invalidateQueries({ queryKey: ['learning-areas'] });
    },
  });
};

// Learning Outcome hooks
export const useLearningOutcomes = (filters?: { learning_area_id?: string }, enabled = true) => {
  return useQuery<LearningOutcome[]>({
    queryKey: ['learning-outcomes', filters?.learning_area_id],
    queryFn: () => learningOutcomeService.getLearningOutcomes(filters?.learning_area_id),
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: enabled,
  });
};

export const useLearningOutcome = (id: string, enabled = true) => {
  return useQuery<LearningOutcome>({
    queryKey: ['learning-outcome', id],
    queryFn: () => learningOutcomeService.getLearningOutcome(id),
    enabled: enabled && !!id,
  });
};

export const useCreateLearningOutcome = () => {
  const queryClient = useQueryClient();
  
  return useMutation<LearningOutcome, Error, LearningOutcomeCreate>({
    mutationFn: learningOutcomeService.createLearningOutcome,
    onSuccess: (newOutcome) => {
      // Invalidate and refetch outcomes
      queryClient.invalidateQueries({ queryKey: ['learning-outcomes'] });
      // Update the cache with the new outcome
      queryClient.setQueryData(['learning-outcome', newOutcome.id], newOutcome);
    },
  });
};

export const useUpdateLearningOutcome = () => {
  const queryClient = useQueryClient();
  
  return useMutation<LearningOutcome, Error, { id: string; data: LearningOutcomeUpdate }>({
    mutationFn: ({ id, data }) => learningOutcomeService.updateLearningOutcome(id, data),
    onSuccess: (updatedOutcome) => {
      // Update the cache
      queryClient.setQueryData(['learning-outcome', updatedOutcome.id], updatedOutcome);
      // Invalidate outcomes list
      queryClient.invalidateQueries({ queryKey: ['learning-outcomes'] });
    },
  });
};

export const useDeleteLearningOutcome = () => {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, string>({
    mutationFn: learningOutcomeService.deleteLearningOutcome,
    onSuccess: (_, outcomeId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: ['learning-outcome', outcomeId] });
      // Invalidate outcomes list
      queryClient.invalidateQueries({ queryKey: ['learning-outcomes'] });
    },
  });
};

// Learning Period hooks
export const useLearningPeriods = () => {
  return useQuery<LearningPeriod[]>({
    queryKey: ['learning-periods'],
    queryFn: learningPeriodService.getLearningPeriods,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useLearningPeriod = (id: string, enabled = true) => {
  return useQuery<LearningPeriod>({
    queryKey: ['learning-period', id],
    queryFn: () => learningPeriodService.getLearningPeriod(id),
    enabled: enabled && !!id,
  });
};

export const useCreateLearningPeriod = () => {
  const queryClient = useQueryClient();
  
  return useMutation<LearningPeriod, Error, LearningPeriodCreate>({
    mutationFn: learningPeriodService.createLearningPeriod,
    onSuccess: (newPeriod) => {
      // Invalidate and refetch periods
      queryClient.invalidateQueries({ queryKey: ['learning-periods'] });
      // Update the cache with the new period
      queryClient.setQueryData(['learning-period', newPeriod.id], newPeriod);
    },
  });
};

export const useUpdateLearningPeriod = () => {
  const queryClient = useQueryClient();
  
  return useMutation<LearningPeriod, Error, { id: string; data: LearningPeriodUpdate }>({
    mutationFn: ({ id, data }) => learningPeriodService.updateLearningPeriod(id, data),
    onSuccess: (updatedPeriod) => {
      // Update the cache
      queryClient.setQueryData(['learning-period', updatedPeriod.id], updatedPeriod);
      // Invalidate periods list
      queryClient.invalidateQueries({ queryKey: ['learning-periods'] });
    },
  });
};

export const useDeleteLearningPeriod = () => {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, string>({
    mutationFn: learningPeriodService.deleteLearningPeriod,
    onSuccess: (_, periodId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: ['learning-period', periodId] });
      // Invalidate periods list
      queryClient.invalidateQueries({ queryKey: ['learning-periods'] });
    },
  });
};
